import { useState, useEffect, useRef } from 'react';
import { Button, Chip, Counter, GlitchText, Panel } from '../ui';
import './VictoryScreen.css';

// The async Clipboard API is commonly blocked by the iframe's permissions
// policy inside Discord Activities and fails silently (the promise just
// rejects). The legacy execCommand path isn't gated the same way, so it's
// used as a fallback rather than leaving the user with no way to copy.
async function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.warn('[VictoryScreen] navigator.clipboard.writeText failed, falling back:', err);
    }
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textarea);
    return success;
  } catch (err) {
    console.error('[VictoryScreen] execCommand fallback failed:', err);
    return false;
  }
}

function VictoryScreen({ title, guessCount, auth, nextArticleIn, onClose }) {
  const [countdown, setCountdown] = useState(nextArticleIn);
  const [copyStatus, setCopyStatus] = useState('idle'); // 'idle' | 'copied' | 'failed'
  // Tracks whether the post has been *initiated*, checked synchronously so
  // React.StrictMode's dev-mode double-invoke (mount -> cleanup -> mount)
  // can't fire the request twice - state alone isn't enough since both
  // invocations see the same stale `false` before either commits.
  const postStartedRef = useRef(false);

  // Update countdown
  useEffect(() => {
    if (!countdown) return;

    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1000) return 0;
        return prev - 1000;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [countdown]);

  // Format countdown timer
  const formatCountdown = (ms) => {
    if (!ms) return '00:00:00';
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((ms % (1000 * 60)) / 1000);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  // Auto-post win message to Discord channel on mount.
  // Requires the user to have launched via the entry point command, which
  // captures an interaction token server-side (see /api/discord/interactions).
  useEffect(() => {
    if (!auth?.user || postStartedRef.current) return;
    postStartedRef.current = true;

    const postWinMessage = async () => {
      try {
        const payload = {
          userId: auth.user.id,
          username: auth.user.global_name || auth.user.username,
          guessCount,
          title,
        };

        const response = await fetch('/api/discord/post-win', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('[VictoryScreen] Failed to post win message:', response.status, errorText);
        }
      } catch (err) {
        console.error('[VictoryScreen] Exception while posting win message:', err);
      }
    };

    postWinMessage();
  }, [auth, guessCount, title]);

  // Manual fallback for when the automatic post above didn't happen (e.g.
  // launched outside the entry point command, or the 15-minute interaction
  // window already passed) - copies a pre-written result to the clipboard
  // so the user can paste it into the channel themselves. Never include the
  // title - that would spoil the article for anyone who hasn't played yet.
  const handleCopyResults = async () => {
    const text = `I found today's Decrypedia article in ${guessCount} guesses!`;
    const success = await copyToClipboard(text);
    setCopyStatus(success ? 'copied' : 'failed');
    setTimeout(() => setCopyStatus('idle'), 2000);
  };

  const copyLabel = {
    copied: 'Copied!',
    failed: 'Copy failed',
    idle: 'Copy results',
  }[copyStatus];

  return (
    <div className="victory">
      {/* Radiating light behind the panel — the "you did it" halo. */}
      <div className="victory__rays" aria-hidden="true" />

      {/* No corner tag here: it would sit in the same top-right corner as the
          close button. The heading already says the article is solved. */}
      <Panel variant="hot" className="victory__panel">
        <Button
          variant="ghost"
          size="sm"
          className="victory__close ui-btn--icon"
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </Button>

        <GlitchText as="h2" className="victory__heading" intensity="extreme" tone="gold">
          DECRYPTED
        </GlitchText>

        <p className="victory__lede">Today&apos;s article was</p>

        <h3 className="victory__title">{title}</h3>

        <div className="victory__scoreline">
          <Counter value={guessCount} label="Guesses" tone="gold" size="lg" />
        </div>

        <div className="victory__actions">
          <Button variant="gold" size="lg" onClick={handleCopyResults} block>
            {copyLabel}
          </Button>
        </div>

        {countdown > 0 && (
          <div className="victory__next">
            <Chip tone="cyan" icon="⏱">
              Next article in <span className="u-digits">{formatCountdown(countdown)}</span>
            </Chip>
          </div>
        )}
      </Panel>
    </div>
  );
}

export default VictoryScreen;
